/**
 * Catch URLs shaped like the course-authoring micro-frontend's and forward them.
 *
 * Verawood deleted its server-rendered authoring pages, but it kept the redirects to
 * them: `/course/<key>`, `/settings/grading/<key>`, `/assets/<key>` and friends all still
 * resolve on Studio and all 302 to `COURSE_AUTHORING_MICROFRONTEND_URL`, using the MFE's
 * URL shape rather than ours. Our Tutor plugin points that setting at this app, so those
 * redirects arrive here — as `/course/<key>/settings/grading`, not
 * `/admin/courses/<key>/grading`.
 *
 * Translating them here rather than adopting the MFE's URL shape wholesale keeps one
 * information architecture in the app (`/admin/...` is where staff tools live) while still
 * honouring every link the platform generates — including ones inside Studio pages and
 * emails that we do not control and cannot rewrite.
 *
 * Anything unrecognised goes to the course outline instead of 404ing. An author who
 * followed a real link should always land somewhere useful, and the outline is the one
 * page every authoring journey passes through.
 */

import { Navigate, useParams } from 'react-router-dom';

/** MFE path tail → our route segment. Only tails we have actually built are listed. */
const DESTINATIONS: Record<string, string> = {
  'settings/details': 'settings',
  'settings/grading': 'grading',
  certificates: 'certificates',
};

export function LegacyAuthoringRedirect() {
  const params = useParams();
  const courseId = params.courseId ?? '';
  // The splat holds whatever followed the course key, e.g. "settings/grading".
  const tail = (params['*'] ?? '').replace(/^\/+|\/+$/g, '');

  if (!courseId) return <Navigate to="/admin/courses" replace />;

  const segment = DESTINATIONS[tail] ?? '';
  const base = `/admin/courses/${encodeURIComponent(courseId)}`;
  return <Navigate to={segment ? `${base}/${segment}` : base} replace />;
}

/**
 * The MFE addresses a unit as `/container/<usage key>`, with no course key in the path.
 * Ours needs both, and the course key is recoverable: a usage key embeds it, differing
 * only in prefix and in carrying a block on the end.
 *
 *   block-v1:ORG+NUM+RUN+type@vertical+block@abc  ->  course-v1:ORG+NUM+RUN
 */
export function courseKeyFromUsageKey(usageKey: string): string | null {
  const match = /^block-v1:(.+?)\+type@/.exec(usageKey);
  return match?.[1] ? `course-v1:${match[1]}` : null;
}

export function LegacyContainerRedirect() {
  const { blockId = '' } = useParams();
  const courseId = courseKeyFromUsageKey(blockId);

  if (!courseId) return <Navigate to="/admin/courses" replace />;
  return (
    <Navigate
      to={`/admin/courses/${encodeURIComponent(courseId)}/units/${encodeURIComponent(blockId)}`}
      replace
    />
  );
}
